import { Check, Loader2, Upload } from 'lucide-react'
import {
  documentStatusLabel,
  formatDocDateTime,
  humanizeDocError,
  type UiDocumentItem,
} from '@/lib/documents-ui'

type DocumentPickerPanelProps = {
  selectedDocIds: string[]
  documents: UiDocumentItem[]
  docsLoading: boolean
  docsUploading: boolean
  docsError?: string | null
  processingInfo?: string | null
  acceptAttr: string
  maxUploadMb: number
  uploadButtonTitle: string
  emptyText?: string
  onUploadClick: () => void
  onRefresh: () => void
  onToggleDocument: (doc: UiDocumentItem) => void
  onClose?: () => void
  inSheet?: boolean
}

export function DocumentPickerPanel({
  selectedDocIds,
  documents,
  docsLoading,
  docsUploading,
  docsError,
  processingInfo,
  acceptAttr,
  maxUploadMb,
  uploadButtonTitle,
  emptyText,
  onUploadClick,
  onRefresh,
  onToggleDocument,
  onClose,
  inSheet = false,
}: DocumentPickerPanelProps) {
  return (
    <div
      className={inSheet ? '' : 'glass-dropdown rounded-2xl'}
      style={{ maxHeight: inSheet ? '62vh' : '38vh', overflowY: 'auto', padding: '10px' }}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <div>
          <p className="text-[11px] font-semibold">Выбор документов для чата</p>
          <p className="text-[10px] text-telegram-hint">Выбрано: {selectedDocIds.length}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onUploadClick}
            disabled={docsUploading}
            className="rounded-lg bg-telegram-button text-white px-2.5 py-1.5 text-[10px] font-semibold flex items-center gap-1 active:scale-95 transition-transform disabled:opacity-40"
            title={uploadButtonTitle}
          >
            {docsUploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
            <span>Загрузить новый документ</span>
          </button>
          <button
            onClick={onRefresh}
            className="text-[10px] text-telegram-button active:scale-95 transition-transform"
            disabled={docsLoading}
          >
            {docsLoading ? 'Обновляю…' : 'Обновить'}
          </button>
          {onClose ? (
            <button
              onClick={onClose}
              className="text-[10px] text-telegram-button active:scale-95 transition-transform"
            >
              Готово
            </button>
          ) : null}
        </div>
      </div>

      <p className="text-[10px] text-telegram-hint mb-2">Разрешено: {acceptAttr} • до {maxUploadMb} MB</p>
      {processingInfo ? (
        <p className="text-[10px] text-telegram-button mb-2">{processingInfo}</p>
      ) : null}

      {docsError ? (
        <p className="text-[10px] text-red-500 mb-2 truncate">{docsError}</p>
      ) : null}

      {documents.length === 0 ? (
        <p className="text-[11px] text-telegram-hint">{emptyText || 'Документы не найдены.'}</p>
      ) : null}

      <div className="flex flex-col gap-1">
        {documents.map((doc) => {
          const docId = String(doc.id || '').trim()
          if (!docId) return null
          const ready = String(doc.status || '').toLowerCase() === 'ready'
          const selected = selectedDocIds.includes(docId)
          const uploadedAt = formatDocDateTime(doc.created_at)
          return (
            <button
              key={docId}
              onClick={() => onToggleDocument(doc)}
              disabled={!ready}
              className="w-full text-left rounded-lg px-2 py-1.5 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: selected ? 'rgba(36,129,204,0.12)' : 'rgba(127,127,127,0.08)' }}
            >
              <div className="flex items-start gap-2">
                <span
                  className="shrink-0 rounded-md flex items-center justify-center"
                  style={{
                    width: '18px',
                    height: '18px',
                    border: selected ? 'none' : '1px solid var(--glass-border)',
                    background: selected ? 'var(--tg-theme-button-color, #2481cc)' : 'transparent',
                    color: selected ? 'var(--tg-theme-button-text-color, #fff)' : 'var(--tg-theme-hint-color, #999)',
                  }}
                >
                  {selected ? <Check size={11} /> : null}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[12px] font-medium truncate">{doc.filename}</span>
                  <span className="block text-[10px] text-telegram-hint truncate">
                    {doc.scope === 'chat' ? 'Чат' : doc.scope === 'contract' ? 'Контракт' : 'Глобально'}
                    {typeof doc.size_bytes === 'number' ? ` • ${Math.round(doc.size_bytes / 1024)} KB` : ''}
                    {uploadedAt ? ` • ${uploadedAt}` : ''}
                  </span>
                  {humanizeDocError(doc.error_message) ? (
                    <span className="block text-[10px] text-red-500 truncate">{humanizeDocError(doc.error_message)}</span>
                  ) : null}
                </span>
                <span
                  className="shrink-0 rounded-full px-2 py-0.5 text-[9px]"
                  style={{
                    background: ready ? 'var(--tg-theme-button-color, #2481cc)' : 'rgba(127,127,127,0.2)',
                    color: ready ? 'var(--tg-theme-button-text-color, #fff)' : 'var(--tg-theme-hint-color, #999)',
                  }}
                >
                  {documentStatusLabel(doc.status)}
                </span>
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}
